<template>
	<div
		v-show="
			!!scrollInfo.thumbSize &&
			(fixedThumb ? isWrapperIntersecting : true)
		"
		:class="[
			'scrollbar__thumbPlaceholder',
			`scrollbar__thumbPlaceholder--${type}`,
			{
				['scrollbar__thumbPlaceholder--autoHide']: autoHide,
				['scrollbar__thumbPlaceholder--autoExpand']: autoExpand,
			},
		]"
		:style="{
			width: type === 'horizontal' ? `${scrollInfo.thumbSize}px` : '',
			height: type === 'vertical' ? `${scrollInfo.thumbSize}px` : '',
			position: !shouldFixed ? 'absolute' : 'fixed',
			[type === 'vertical' ? 'top' : 'left']: !shouldFixed
				? '3px'
				: `${scrollInfo.boundaryDistance + 3}px`,
		}"
		ref="thumbEl"
		@pointerdown.stop="handlePointerDown"
	>
		<div
			v-once
			:class="['scrollbar__thumb', `scrollbar__thumb--${type}`]"
		/>
	</div>
	<div
		v-if="props.fixedThumb"
		v-show="!!scrollInfo.thumbSize"
		ref="shepherdEl"
		:class="['scrollbar__shepherd', `scrollbar__shepherd--${type}`]"
	/>
</template>

<script setup lang="ts">
import { throttle } from "lodash-es";
import { computed, onUnmounted, shallowRef, watch } from "vue";

interface Props {
	type: "horizontal" | "vertical";
	autoExpand: boolean;
	autoHide: boolean;
	autoHideDelay: number;
	fixedThumb?: boolean;
	scrollInfo: {
		thumbSize: number;
		contentMainSize: number;
		wrapperMainSize: number;
		boundaryDistance: number;
	};
	thumbWidth?: number;
	wrapperEl: Element;
}
const props = defineProps<Props>();
const computedThumbWidth = computed(
	(): string => `${props.thumbWidth ?? 12}px`,
);

/** <--------------- mouse drag handler ---------------> */
let autoHideTimer: ReturnType<typeof setTimeout> | null = null;
const thumbEl = shallowRef<HTMLDivElement>(null!);
const pointerDownInfo = { pageX: 0, pageY: 0, scrollPos: 0 };
let pointerId: number | null = null;

const startAutoHideTimer = (): void => {
	autoHideTimer = setTimeout((): void => {
		if (thumbEl.value)
			thumbEl.value.classList.remove(
				"scrollbar__thumbPlaceholder--scrolling",
			);
		autoHideTimer = null;
	}, props.autoHideDelay);
};

const clearAutoHideTimer = (): void => {
	if (autoHideTimer !== null) clearTimeout(autoHideTimer);
};

onUnmounted(clearAutoHideTimer);

const handlePointerMove = throttle((evt: PointerEvent): void => {
	evt.stopPropagation();
	evt.preventDefault();
	const moveDirection = props.type === "horizontal" ? "pageX" : "pageY";
	const moveDistance =
		((evt[moveDirection] - pointerDownInfo[moveDirection]) /
			props.scrollInfo.wrapperMainSize) *
		props.scrollInfo.contentMainSize;
	props.wrapperEl.scrollTo({
		[props.type === "horizontal" ? "left" : "top"]:
			pointerDownInfo.scrollPos + moveDistance,
		behavior: "auto",
	});
}, 16);

const handlePointerEnd = (): void => {
	startAutoHideTimer();

	thumbEl.value.removeEventListener("pointermove", handlePointerMove);
	thumbEl.value.removeEventListener("pointerup", handlePointerEnd);
	thumbEl.value.removeEventListener("pointercancel", handlePointerEnd);
	thumbEl.value.removeEventListener("mousewheel", handlePointerEnd);
	document.removeEventListener("mousewheel", handlePointerEnd);
	if (typeof pointerId === "number")
		thumbEl.value.releasePointerCapture(pointerId);
	pointerId = null;
};

const handlePointerDown = (evt: PointerEvent): void => {
	if (evt.ctrlKey || evt.button !== 0) return;
	clearAutoHideTimer();

	pointerDownInfo.pageX = evt.pageX;
	pointerDownInfo.pageY = evt.pageY;
	pointerDownInfo.scrollPos =
		props.wrapperEl[
			props.type === "horizontal" ? "scrollLeft" : "scrollTop"
		];

	pointerId = evt?.pointerId;
	thumbEl.value.setPointerCapture(pointerId);

	thumbEl.value.addEventListener("pointermove", handlePointerMove);
	thumbEl.value.addEventListener("pointerup", handlePointerEnd);
	thumbEl.value.addEventListener("pointercancel", handlePointerEnd);
	thumbEl.value.addEventListener("mousewheel", handlePointerEnd, {
		passive: false,
	});
	document.addEventListener("mousewheel", handlePointerEnd, {
		passive: false,
	}); // cancel drag on mousewheel
	thumbEl.value.classList.add("scrollbar__thumbPlaceholder--scrolling");
};

const autoHideAfterScroll = (): void => {
	clearAutoHideTimer();
	if (thumbEl.value)
		thumbEl.value.classList.add("scrollbar__thumbPlaceholder--scrolling");
	startAutoHideTimer();
};

defineExpose({
	autoHideAfterScroll,
});

/** <--------------- fixed Thumb ---------------> */
const isWrapperIntersecting = shallowRef<boolean>(false);
const isShepherdIntersecting = shallowRef<boolean>(false);
const shepherdEl = shallowRef<HTMLElement>(null!);
let shepherdIO: IntersectionObserver | null = null;
let wrapperIO: IntersectionObserver | null = null;
const shouldFixed = computed(
	(): boolean => props.fixedThumb && !isShepherdIntersecting.value,
);

type IntersectionObserverCallback = globalThis.IntersectionObserverCallback;
const shepherdIOCallback: IntersectionObserverCallback = ([
	entry,
]: IntersectionObserverEntry[]): void => {
	isShepherdIntersecting.value = entry.isIntersecting;
};

const wrapperIOCallback: IntersectionObserverCallback = ([
	entry,
]: IntersectionObserverEntry[]): void => {
	isWrapperIntersecting.value = entry.isIntersecting;
};

const clearIO = (): void => {
	if (shepherdIO) {
		shepherdIO.disconnect();
		shepherdIO = null;
	}
	if (wrapperIO) {
		wrapperIO.disconnect();
		wrapperIO = null;
	}
};

watch(
	(): [boolean, Element] => [props.fixedThumb, props.wrapperEl],
	(): void => {
		if (!props.fixedThumb || !props.wrapperEl) return clearIO();
		clearIO();
		wrapperIO = new IntersectionObserver(wrapperIOCallback, {
			threshold: [0, 0.5],
		});
		wrapperIO.observe(props.wrapperEl);
		shepherdIO = new IntersectionObserver(shepherdIOCallback);
		shepherdIO.observe(shepherdEl.value);
	},
);

onUnmounted(clearIO);
</script>

<style lang="scss">
.scrollbar__thumbPlaceholder {
	display: inline-flex;
	position: absolute;
	justify-content: center;
	align-items: center;
	transition: opacity 275ms ease;
	cursor: pointer;
	contain: strict;
	user-select: none;

	&--vertical {
		top: 3px;
		right: 0;

		width: v-bind(computedThumbWidth);
	}
	&--horizontal {
		bottom: 0;
		left: 3px;

		height: v-bind(computedThumbWidth);
	}

	&--autoHide {
		opacity: 0;
	}

	&--scrolling {
		opacity: 1;
	}

	&:hover {
		> .scrollbar__thumb {
			opacity: 1;
		}
	}
	&--autoExpand {
		&:hover {
			> .scrollbar__thumb {
				&--vertical {
					transform: scaleX(1.5);
				}
				&--horizontal {
					transform: scaleY(1.5);
				}
			}
		}
	}
}

.scrollbar__thumb {
	opacity: 0.6;
	transition:
		opacity 275ms ease,
		transform 200ms ease;
	contain: strict;

	border-radius: 4px;
	background-color: rgba(9, 30, 66, 0.5);
	&--vertical {
		width: 66.667%;
		height: 100%;
	}
	&--horizontal {
		width: 100%;
		height: 66.667%;
	}
}

.scrollbar__shepherd {
	position: absolute;
	contain: strict;
	&--vertical {
		top: 0;
		right: 0;
		width: 0%;
		height: 1000;
	}
	&--horizontal {
		bottom: 0;
		left: 0;
		width: 100%;
		height: 0%;
	}
}
</style>
